import type Quill from 'quill';
import type { Range } from 'quill';
import type TableModule from 'quill/modules/table';
import {
  RICH_TEXT_INLINE_FORMATS,
  RICH_TEXT_MAX_INDENT,
  RICH_TEXT_VARIABLE_PATTERN,
} from './rich-text-editor.constants';
import { RichTextCommand, RichTextTableAction } from './rich-text-editor.models';

/**
 * Aplica no Quill um comando da barra de ferramentas.
 *
 * Funções puras sobre a instância (sem Angular), para o componente ficar só com o ciclo de vida.
 * Tudo grava com a origem `user`, para entrar no histórico (desfazer/refazer) e disparar o
 * `text-change` que alimenta o formulário.
 *
 * @param quill Instância já criada.
 * @param command O que a barra pediu.
 * @param range Seleção a usar. A barra rouba o foco ao abrir menus, então o componente guarda a
 *   última seleção e a devolve aqui.
 */
export function applyRichTextCommand(quill: Quill, command: RichTextCommand, range: Range): void {
  switch (command.type) {
    case 'undo':
      quill.history.undo();
      return;
    case 'redo':
      quill.history.redo();
      return;
    case 'format':
      quill.format(command.name, command.value, 'user');
      return;
    case 'indent':
      applyIndent(quill, range, command.step);
      return;
    case 'link':
      applyLink(quill, range, command.url);
      return;
    case 'table':
      applyTableAction(quill, command.action);
      return;
    case 'variable':
      insertVariable(quill, range, command.key);
      return;
    case 'clearFormatting':
      clearInlineFormatting(quill, range);
      return;
  }
}

/** Chaves das variáveis `{{...}}` que aparecem no texto, sem repetição e na ordem do texto. */
export function findVariableKeys(text: string): string[] {
  const keys = new Set<string>();
  for (const match of text.matchAll(RICH_TEXT_VARIABLE_PATTERN)) {
    keys.add(match[1]);
  }
  return [...keys];
}

/** Mesmo cálculo do handler `indent` da toolbar do Quill, limitado a 0..8. */
function applyIndent(quill: Quill, range: Range, step: 1 | -1): void {
  const current = Number(quill.getFormat(range)['indent'] ?? 0) || 0;
  const next = Math.min(RICH_TEXT_MAX_INDENT, Math.max(0, current + step));
  quill.format('indent', next || false, 'user');
}

/**
 * Cria, troca ou remove o link.
 *
 * Com seleção, aplica sobre ela. Sem seleção: dentro de um link, age sobre o link inteiro; fora,
 * insere o próprio endereço como texto do link.
 */
function applyLink(quill: Quill, range: Range, url: string | null): void {
  const target = range.length > 0 ? range : (findLinkRange(quill, range.index) ?? range);

  if (!url) {
    if (target.length > 0) quill.formatText(target.index, target.length, 'link', false, 'user');
    return;
  }

  if (target.length > 0) {
    quill.formatText(target.index, target.length, 'link', url, 'user');
    return;
  }

  quill.insertText(range.index, url, 'link', url, 'user');
  quill.setSelection(range.index + url.length, 0, 'silent');
}

/** Trecho do link que contém a posição, ou `null` se a posição não está num link. */
function findLinkRange(quill: Quill, index: number): Range | null {
  const [leaf] = quill.getLeaf(index);
  const parent = leaf?.parent;
  if (!parent || parent.statics.blotName !== 'link') return null;
  return { index: quill.getIndex(parent), length: parent.length() } as Range;
}

/** Ações do módulo `table` do Quill 2. Fora de uma tabela, só "inserir" faz efeito. */
function applyTableAction(quill: Quill, action: RichTextTableAction): void {
  const table = quill.getModule('table') as TableModule | undefined;
  if (!table) return;

  switch (action) {
    case 'insert':
      table.insertTable(2, 2);
      return;
    case 'rowAbove':
      table.insertRowAbove();
      return;
    case 'rowBelow':
      table.insertRowBelow();
      return;
    case 'columnLeft':
      table.insertColumnLeft();
      return;
    case 'columnRight':
      table.insertColumnRight();
      return;
    case 'deleteRow':
      table.deleteRow();
      return;
    case 'deleteColumn':
      table.deleteColumn();
      return;
    case 'deleteTable':
      table.deleteTable();
      return;
  }
}

/** Insere `{{key}}` na posição do cursor (substituindo a seleção, se houver). */
function insertVariable(quill: Quill, range: Range, key: string): void {
  const token = `{{${key}}}`;
  if (range.length > 0) quill.deleteText(range.index, range.length, 'user');
  quill.insertText(range.index, token, 'user');
  quill.setSelection(range.index + token.length, 0, 'silent');
}

/**
 * Remove a formatação inline (negrito, cor, tamanho, link...). Não usa `removeFormat`, que também
 * tira formatos de bloco e desmontaria uma tabela ou lista.
 */
function clearInlineFormatting(quill: Quill, range: Range): void {
  const formats = Object.fromEntries(RICH_TEXT_INLINE_FORMATS.map(name => [name, false]));

  if (range.length > 0) {
    quill.formatText(range.index, range.length, formats, 'user');
    return;
  }

  // Sem seleção, zera a formatação que seria aplicada ao próximo caractere digitado.
  RICH_TEXT_INLINE_FORMATS.forEach(name => quill.format(name, false, 'user'));
}
